import { _ } from "../../../../core/polyglot/polyglot.js";
import {
	caretDownIcon,
	caretRightIcon,
} from "../../../../core/elements/icons/index.js";
import AbstractHtmlEditorColumnRenderer from "./AbstractHtmlEditorColumnRenderer.js";
import AbstractRow from "../../../../models/reports/reports/rows/AbstractRow.js";
import AccountGroupRow from "../../../../models/reports/reports/rows/AccountGroupRow.js";
import AccountRow from "../../../../models/reports/reports/rows/AccountRow.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import CssClassNameVisitor from "../../../../models/reports/reports/CssClassNameVisitor.js";
import FormulaRow from "../../../../models/reports/reports/rows/FormulaRow.js";
import GeneratedAccountGroupRow from "../../../../models/reports/reports/rows/GeneratedAccountGroupRow.js";
import KeyFigureRow from "../../../../models/reports/reports/rows/KeyFigureRow.js";
import SpacerRow from "../../../../models/reports/reports/rows/SpacerRow.js";
import TextRow from "../../../../models/reports/reports/rows/TextRow.js";

/**
 * Visitor that renders single cells in the title column of a report editor.
 *
 * @param {function} [spec.onSectionToggled] - Called when a group is
 * expanded or collapsed.
 */
export default class HtmlEditorTitleColumnRenderer extends AbstractHtmlEditorColumnRenderer {
	constructor({ onSectionToggled = () => {} } = {}) {
		super(...arguments);
		this._onSectionToggled = onSectionToggled;
		this._cssClassNameRenderer = new CssClassNameVisitor();
	}

	visitAbstractRow(row) {
		return this._rowRenderer(
			row,
			this._cssClassNameRenderer.visit(row),
			this._iconRenderer(row),
			row.labelRenderer(),
		);
	}

	_rowRenderer(row, cssClass, ...contents) {
		return (html) => {
			const div = html.div(
				{
					klass: [
						cssClass,
						"title",
						"report-row",
						this._titleCssClass(row),
						this._rowVisibilityCssClass(row),
						this._getDepthCssClass(row),
					],
					reportid: this._getRowId(row),
					"data-test": "report-row",
				},
				...contents,
				this._visibilityIconRenderer(row),
				this._dimensionIconRenderer(row),
			);

			if (row.canBeSelected()) {
				div.click(() => this._toggleSelection(row));
			}
		};
	}

	_headerRowRenderer(row) {
		return this._rowRenderer(
			row,
			"report-group-title",
			this._caretRenderer(row),
			this._iconRenderer(row),
			row.getTitle(),
		);
	}

	_subRowsRenderer(row, subrows) {
		return (html) =>
			html.div(
				{ klass: this._dragAndDropCssClasses(row) },
				this.visitAll(subrows),
			);
	}

	_summaryRowRenderer(row) {
		return this._rowRenderer(
			row,
			"summary",
			_("Sum {title}", { title: row.getTitle() }),
		);
	}

	_caretRenderer(row) {
		if (!row.canBeCollapsed() || !row.hasRows()) {
			return () => {};
		}

		return button(
			{
				as: "div",
				onClick: (event) => {
					this._toggleSection(row);
					// Prevent bubbling up the event, which would toggle the
					// selection
					event.preventDefault();
					event.stopPropagation();
				},
			},
			row.isCollapsed()
				? caretRightIcon({
						class: "toggle",
					})
				: caretDownIcon({
						class: "toggle",
					}),
		);
	}

	_dragAndDropCssClasses(group) {
		return group.isAccountGroupRow()
			? ""
			: "draggable row-draggable row-droppable";
	}

	_toggleSection(group) {
		group.getTemplate().toggleCollapsed();
		group.toggleCollapsed();
		this._onSectionToggled(group);
	}
}

AbstractRow.prototype.iconRenderer = function () {
	return "";
};

AbstractRow.prototype.labelRenderer = function () {
	return this.getTitle();
};

AccountGroupRow.prototype.iconRenderer = function () {
	return "Σ";
};

AccountRow.prototype.labelRenderer = function () {
	return (html) => {
		html.span({ klass: "account-number" }, this.getAccountNumber());
		html.render(this.getTitle());
	};
};

FormulaRow.prototype.iconRenderer = function () {
	return "∫";
};

FormulaRow.prototype.labelRenderer = function () {
	return (html) => html.span({ klass: this.getStyle() }, this.getTitle());
};

GeneratedAccountGroupRow.prototype.iconRenderer = function () {
	return "";
};

KeyFigureRow.prototype.iconRenderer = function () {
	return (html) => html.i({ klass: "fas fa-chart-line" });
};

KeyFigureRow.prototype.labelRenderer = function () {
	return (html) => html.span({ klass: this.getStyle() }, this.getTitle());
};

TextRow.prototype.labelRenderer = function () {
	return (html) => html.span({ klass: this.getStyle() }, this.getTitle());
};

SpacerRow.prototype.labelRenderer = function () {
	return "";
};

GeneratedAccountGroupRow.prototype.iconRenderer = function () {
	return (html) => {
		if (!this.isExcluded()) {
			return;
		}
		html.i({ klass: "fa fa-minus-circle" });
	};
};
